import type { EventMode } from "@verity-connect/domain";

export const ATTENDEE_DESKTOP_QUERY = "(min-width: 64rem)";

export type AttendeeDesktopSurface = "standard" | "preparation-home" | "active-landing" | "live-browser";

export interface AttendeeDesktopSurfaceInput {
  desktop: boolean;
  nativeShell: boolean;
  mode: EventMode;
  pathname: string;
  continuedInBrowser: boolean;
}

/**
 * The attendee desktop split is presentation only. Native shells, compact browsers,
 * personal account routes, and every lifecycle outside the active Event keep the
 * existing participant experience.
 */
export function attendeeDesktopSurface(input: AttendeeDesktopSurfaceInput): AttendeeDesktopSurface {
  if (input.nativeShell || !input.desktop) {
    return "standard";
  }
  if (input.mode === "preparation" && input.pathname === "/") return "preparation-home";
  if (input.mode !== "active" || !isAttendeeEventRoute(input.pathname)) return "standard";
  if (input.pathname === "/" && !input.continuedInBrowser) return "active-landing";
  return "live-browser";
}

/** Public Event routes that belong inside the deliberate desktop browser fallback. */
export function isAttendeeEventRoute(pathname: string): boolean {
  return pathname === "/"
    || pathname === "/schedule" || pathname.startsWith("/schedule/")
    || pathname === "/my-wvcea" || pathname.startsWith("/my-wvcea/")
    || pathname === "/my-stuff" || pathname === "/my-convention"
    || pathname === "/map" || pathname === "/explore"
    || pathname === "/updates"
    || pathname === "/more" || pathname.startsWith("/more/")
    || pathname === "/help";
}

export type AttendeeLiveNavId = "today" | "schedule" | "plan" | "maps" | "updates";

export const ATTENDEE_LIVE_NAV: readonly { id: AttendeeLiveNavId; label: string; href: string }[] = [
  { id: "today", label: "Today", href: "/" },
  { id: "schedule", label: "Full schedule", href: "/schedule" },
  { id: "plan", label: "My plan", href: "/schedule?day=mine" },
  { id: "maps", label: "Maps", href: "/map" },
  { id: "updates", label: "Updates", href: "/updates" },
];

export function attendeeLiveNavActive(pathname: string, search: string): AttendeeLiveNavId | null {
  if (pathname === "/") return "today";
  if (pathname === "/schedule" || pathname.startsWith("/schedule/")) {
    return pathname === "/schedule" && new URLSearchParams(search).get("day") === "mine" ? "plan" : "schedule";
  }
  if (pathname === "/map" || pathname === "/explore") return "maps";
  if (pathname === "/updates") return "updates";
  return null;
}

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function continuationKey(eventSlug: string): string {
  return `connect:attendee-live-browser:${eventSlug}`;
}

export function readAttendeeLiveContinuation(eventSlug: string, storage: StorageLike | null): boolean {
  try {
    return storage?.getItem(continuationKey(eventSlug)) === "true";
  } catch {
    return false;
  }
}

export function writeAttendeeLiveContinuation(eventSlug: string, continued: boolean, storage: StorageLike | null): void {
  try {
    if (!storage) return;
    if (continued) storage.setItem(continuationKey(eventSlug), "true");
    else storage.removeItem(continuationKey(eventSlug));
  } catch {
    // The in-memory React state still carries the choice for this page when storage is unavailable.
  }
}
